// MessengerPress — operation catalog for the structured preset editor.
//
// Mirrors pdfsnake's operation set 1:1 (cards, grid, booklet, resize,
// crop, overlay, shuffle, rotate, flip) plus our two custom ops:
// shiftContent (for double-sided register) and the auto-appended
// flatten step (which is intentionally NOT in this catalog so the
// client can't add it manually).
//
// Single source of truth for which operations exist, what fields they
// take, what units the fields are in, and what the dropdown labels look
// like. The PresetEditor component renders inputs by walking this list,
// so adding a new op type only requires adding an entry here AND a
// matching Zod schema in
// apps/mdl-backend/src/services/messenger-press/presetSchema.ts.

export type FieldKind =
  | 'number'
  | 'integer'
  | 'enum'
  | 'mask'
  | 'boolean'
  | 'integer-array'
  | 'string-array'

export type FieldDef = {
  name: string
  label: string
  kind: FieldKind
  unit?: 'in'
  required?: boolean
  options?: readonly string[] | readonly number[]
  defaultValue?: number | string | boolean
  helpText?: string
}

export type OperationDef = {
  type: string
  label: string
  description: string
  fields: FieldDef[]
}

const PAGE_SELECTOR_OPTIONS = [
  'all',
  'front',
  'back',
  'odd',
  'even',
  'first',
  'last',
] as const

export const OPERATION_DEFS: OperationDef[] = [
  {
    type: 'cards',
    label: 'Cards',
    description:
      'For each source page, output one sheet with N replicated copies. Layout is auto-computed from sheet ÷ card size unless rows/cols are set.',
    fields: [
      {
        name: 'sheetWidth',
        label: 'Sheet width',
        kind: 'number',
        unit: 'in',
        required: true,
      },
      {
        name: 'sheetHeight',
        label: 'Sheet height',
        kind: 'number',
        unit: 'in',
        required: true,
      },
      { name: 'rows', label: 'Rows (optional)', kind: 'integer' },
      { name: 'cols', label: 'Cols (optional)', kind: 'integer' },
      { name: 'up', label: 'Up (display only)', kind: 'integer' },
      { name: 'gutter', label: 'Gutter', kind: 'number', unit: 'in' },
      { name: 'inset', label: 'Sheet inset', kind: 'number', unit: 'in' },
      { name: 'marks', label: 'Crop marks', kind: 'boolean' },
    ],
  },
  {
    type: 'grid',
    label: 'Grid',
    description:
      'Pack source pages sequentially into rows × cols slots. Use when you have many distinct pages to gang onto sheets.',
    fields: [
      { name: 'rows', label: 'Rows', kind: 'integer', required: true },
      { name: 'cols', label: 'Cols', kind: 'integer', required: true },
      {
        name: 'sheetWidth',
        label: 'Sheet width',
        kind: 'number',
        unit: 'in',
        required: true,
      },
      {
        name: 'sheetHeight',
        label: 'Sheet height',
        kind: 'number',
        unit: 'in',
        required: true,
      },
      { name: 'gutter', label: 'Gutter', kind: 'number', unit: 'in' },
      { name: 'inset', label: 'Sheet inset', kind: 'number', unit: 'in' },
      { name: 'marks', label: 'Crop marks', kind: 'boolean' },
    ],
  },
  {
    type: 'booklet',
    label: 'Booklet',
    description:
      'Saddle-stitch 2-up signature imposition. Reorders pages so they print + fold into a booklet.',
    fields: [
      {
        name: 'sheetWidth',
        label: 'Sheet width',
        kind: 'number',
        unit: 'in',
        required: true,
      },
      {
        name: 'sheetHeight',
        label: 'Sheet height',
        kind: 'number',
        unit: 'in',
        required: true,
      },
    ],
  },
  {
    type: 'resize',
    label: 'Resize',
    description:
      'Scale page content to a target width × height (real content scaling, not just box reassignment).',
    fields: [
      {
        name: 'width',
        label: 'Width',
        kind: 'number',
        unit: 'in',
        required: true,
      },
      {
        name: 'height',
        label: 'Height',
        kind: 'number',
        unit: 'in',
        required: true,
      },
    ],
  },
  {
    type: 'crop',
    label: 'Crop',
    description: 'Trim each page by an explicit inset on each edge.',
    fields: [
      { name: 'top', label: 'Top', kind: 'number', unit: 'in', required: true },
      {
        name: 'right',
        label: 'Right',
        kind: 'number',
        unit: 'in',
        required: true,
      },
      {
        name: 'bottom',
        label: 'Bottom',
        kind: 'number',
        unit: 'in',
        required: true,
      },
      {
        name: 'left',
        label: 'Left',
        kind: 'number',
        unit: 'in',
        required: true,
      },
    ],
  },
  {
    type: 'pad',
    label: 'Pad / Canvas',
    description:
      'Expand (or clip) the page canvas to a target size and center content. No scaling — content stays at its current size. Overflow is clipped.',
    fields: [
      {
        name: 'width',
        label: 'Canvas width',
        kind: 'number',
        unit: 'in',
        required: true,
      },
      {
        name: 'height',
        label: 'Canvas height',
        kind: 'number',
        unit: 'in',
        required: true,
      },
    ],
  },
  {
    type: 'overlay',
    label: 'Overlay',
    description:
      'Overlay another PDF (typically a vendor mask) onto every page. The mask file lives in dignity-pdf-masks. Filename may contain the literal token "{themeId}" — at run time it is replaced with the job\'s OrderProduct themeId, so a single preset can dispatch a different mask per theme. Jobs with no themeId fail when the token is present.',
    fields: [
      {
        name: 'filename',
        label: 'Mask file',
        kind: 'mask',
        required: true,
        helpText:
          'Bare filename in the masks bucket. Use "{themeId}.pdf" for theme-dependent masks (resolved at run time from the job\'s themeId).',
      },
    ],
  },
  {
    type: 'shuffle',
    label: 'Shuffle',
    description:
      'Reorder pages by an explicit zero-indexed sequence. Pages not listed are dropped.',
    fields: [
      {
        name: 'order',
        label: 'Order (comma-separated indexes)',
        kind: 'integer-array',
        required: true,
      },
    ],
  },
  {
    type: 'rotate',
    label: 'Rotate',
    description: 'Rotate selected pages by a multiple of 90 degrees.',
    fields: [
      {
        name: 'pageSelector',
        label: 'Apply to',
        kind: 'enum',
        options: PAGE_SELECTOR_OPTIONS,
        required: true,
      },
      {
        name: 'degrees',
        label: 'Degrees',
        kind: 'enum',
        options: ['0', '90', '180', '270'] as const,
        required: true,
      },
    ],
  },
  {
    type: 'flip',
    label: 'Flip',
    description: 'Mirror selected pages horizontally or vertically.',
    fields: [
      {
        name: 'pageSelector',
        label: 'Apply to',
        kind: 'enum',
        options: PAGE_SELECTOR_OPTIONS,
        required: true,
      },
      {
        name: 'axis',
        label: 'Axis',
        kind: 'enum',
        options: ['horizontal', 'vertical'] as const,
        required: true,
      },
    ],
  },
  {
    type: 'shiftContent',
    label: 'Shift content',
    description:
      'Translate the visible content of selected pages without touching the page boxes. Used for double-sided print register.',
    fields: [
      {
        name: 'pageSelector',
        label: 'Apply to',
        kind: 'enum',
        options: PAGE_SELECTOR_OPTIONS,
        required: true,
      },
      {
        name: 'offsetX',
        label: 'X offset',
        kind: 'number',
        unit: 'in',
        helpText: '+ right, − left',
      },
      {
        name: 'offsetY',
        label: 'Y offset',
        kind: 'number',
        unit: 'in',
        helpText: '+ up, − down',
      },
    ],
  },
  {
    type: 'prependPdf',
    label: 'Prepend PDF',
    description:
      'Insert every page of an external PDF at the FRONT of the document. The file must live in the masks bucket (e.g. MDL-Celestial-8up-Append.pdf). When "Only for theme IDs" is set, the op runs only if the job\'s source PDF belongs to one of those themes — leave blank to run on every job.',
    fields: [
      { name: 'filename', label: 'Append file', kind: 'mask', required: true },
      {
        name: 'whenThemeIdIn',
        label: 'Only for theme IDs (comma-separated, optional)',
        kind: 'string-array',
        helpText:
          "Leave blank to always run. When set, the op is a no-op unless the source OrderProduct's themeId is in this list.",
      },
    ],
  },
  {
    type: 'removePages',
    label: 'Remove pages',
    description:
      'Drop a page from the current document. Selector can be "first" (page 0), "last" (final page), or "index" with a zero-based page index. Same theme gate as Prepend PDF.',
    fields: [
      {
        name: 'selector',
        label: 'Selector',
        kind: 'enum',
        options: ['first', 'last', 'index'] as const,
        required: true,
      },
      {
        name: 'index',
        label: 'Index (only when selector = index)',
        kind: 'integer',
      },
      {
        name: 'whenThemeIdIn',
        label: 'Only for theme IDs (comma-separated, optional)',
        kind: 'string-array',
        helpText:
          "Leave blank to always run. When set, the op is a no-op unless the source OrderProduct's themeId is in this list.",
      },
    ],
  },
  // NOTE: `flatten` is intentionally NOT in this catalog. The Ghostscript
  // flatten step is appended automatically at the end of every recipe by
  // RecipeBuilder before save and before run, so the client never has to
  // think about it.
]

// Always-on terminal operation. Never shown to the client.
export const TERMINAL_FLATTEN_OP = { type: 'flatten' } as const

export const operationDefByType = (type: string): OperationDef | undefined =>
  OPERATION_DEFS.find((d) => d.type === type)

// Build a fresh op object for `type` with all required fields populated
// to either their defaults or zero/empty placeholders.
export const blankOperation = (type: string): Record<string, unknown> => {
  const def = operationDefByType(type)
  if (!def) return { type }
  const op: Record<string, unknown> = { type }
  for (const field of def.fields) {
    if (field.defaultValue !== undefined) {
      op[field.name] = field.defaultValue
    } else if (field.kind === 'integer' || field.kind === 'number') {
      op[field.name] = 0
    } else if (field.kind === 'enum') {
      op[field.name] = field.options?.[0] ?? ''
    } else if (field.kind === 'boolean') {
      op[field.name] = false
    } else if (field.kind === 'integer-array') {
      op[field.name] = []
    } else if (field.kind === 'string-array') {
      op[field.name] = []
    } else {
      op[field.name] = ''
    }
  }
  if (def.fields.some((f) => f.unit === 'in')) {
    op.unit = 'in'
  }
  return op
}
